import { supabase } from '../lib/supabase';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker - use CDN with correct .mjs extension for v5.4.149
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.149/build/pdf.worker.min.mjs';

export interface KnowledgeDocument {
  id: string;
  user_id: string;
  business_id?: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  description?: string;
  extracted_content?: string;
  content_preview?: string;
  tags?: string[];
  category?: string;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  processing_error?: string;
  created_at: string;
  updated_at: string;
  processed_at?: string;
}

export interface KnowledgeContext {
  document_name: string;
  category: string;
  content: string;
  tags: string[];
}

class KnowledgeService {
  /**
   * Extract text from PDF file
   */
  async extractTextFromPDF(file: File): Promise<string> {
    try {
      console.log('[PDF] Starting extraction for:', file.name, 'Size:', file.size);
      console.log('[PDF] Worker source:', pdfjsLib.GlobalWorkerOptions.workerSrc);
      console.log('[PDF] PDF.js version:', pdfjsLib.version);
      
      const arrayBuffer = await file.arrayBuffer();
      console.log('[PDF] ArrayBuffer size:', arrayBuffer.byteLength);
      
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      loadingTask.onProgress = (progress: any) => {
        console.log('[PDF] Loading progress:', progress.loaded, '/', progress.total);
      };
      
      const pdf = await loadingTask.promise;
      console.log('[PDF] Document loaded, pages:', pdf.numPages);
      
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        console.log(`[PDF] Extracting page ${i}/${pdf.numPages}`);
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        fullText += pageText + '\n';
      }
      
      console.log('[PDF] Extraction complete, text length:', fullText.length);
      return fullText.trim();
    } catch (error: any) {
      console.error('[PDF] Extraction failed with error:', error);
      console.error('[PDF] Error name:', error?.name);
      console.error('[PDF] Error message:', error?.message);
      console.error('[PDF] Error stack:', error?.stack);
      console.error('[PDF] Error details:', error?.details);
      throw new Error(`Failed to extract text from PDF: ${error?.message || 'Unknown error'}`);
    }
  }

  /**
   * Extract text from plain text file
   */
  async extractTextFromPlainText(file: File): Promise<string> {
    try {
      return await file.text();
    } catch (error) {
      console.error('Text extraction failed:', error);
      throw new Error('Failed to extract text from file');
    }
  }

  /**
   * Extract text based on file type
   */
  async extractTextFromFile(file: File): Promise<string> {
    const fileType = file.type;

    if (fileType === 'application/pdf') {
      return await this.extractTextFromPDF(file);
    } else if (
      fileType === 'text/plain' ||
      fileType === 'text/markdown' ||
      fileType.includes('text/')
    ) {
      return await this.extractTextFromPlainText(file);
    } else if (
      fileType.includes('word') ||
      fileType.includes('document') ||
      fileType.includes('sheet') ||
      fileType.includes('excel')
    ) {
      // For Word/Excel files, we'd need a specialized library
      // For now, return a placeholder
      return `[Content from ${file.name}]\nNote: Advanced document parsing will be available soon. Please convert to PDF for full text extraction.`;
    }

    throw new Error(`Unsupported file type: ${fileType}`);
  }

  /**
   * Auto-categorize document based on filename and content
   */
  categorizeDocument(fileName: string, content: string): string {
    const lowerName = fileName.toLowerCase();
    const lowerContent = content.toLowerCase().substring(0, 1000);

    // Check filename first
    if (lowerName.includes('product') || lowerName.includes('spec')) {
      return 'product';
    } else if (lowerName.includes('market') || lowerName.includes('campaign')) {
      return 'marketing';
    } else if (lowerName.includes('financial') || lowerName.includes('budget') || lowerName.includes('revenue')) {
      return 'financial';
    } else if (lowerName.includes('legal') || lowerName.includes('contract')) {
      return 'legal';
    } else if (lowerName.includes('plan') || lowerName.includes('strategy')) {
      return 'strategy';
    }

    // Check content
    const keywords = {
      product: ['product', 'feature', 'specification', 'technical', 'development'],
      marketing: ['marketing', 'campaign', 'audience', 'brand', 'promotion', 'advertising'],
      financial: ['revenue', 'profit', 'budget', 'financial', 'cost', 'pricing'],
      legal: ['legal', 'contract', 'agreement', 'terms', 'compliance'],
      strategy: ['strategy', 'plan', 'goal', 'objective', 'mission', 'vision']
    };

    for (const [category, words] of Object.entries(keywords)) {
      if (words.some(word => lowerContent.includes(word))) {
        return category;
      }
    }

    return 'general';
  }

  /**
   * Process uploaded document and extract content
   */
  async processDocument(documentId: string, file: File): Promise<void> {
    try {
      // Update status to processing
      await supabase
        .from('business_consultant_knowledge')
        .update({ processing_status: 'processing' })
        .eq('id', documentId);

      // Extract text content
      const extractedContent = await this.extractTextFromFile(file);
      const contentPreview = extractedContent.substring(0, 500);
      const category = this.categorizeDocument(file.name, extractedContent);

      // Update document with extracted content
      await supabase
        .from('business_consultant_knowledge')
        .update({
          extracted_content: extractedContent,
          content_preview: contentPreview,
          category,
          processing_status: 'completed',
          processed_at: new Date().toISOString()
        })
        .eq('id', documentId);

    } catch (error: any) {
      console.error('Document processing failed:', error);
      
      // Update status to failed
      await supabase
        .from('business_consultant_knowledge')
        .update({
          processing_status: 'failed',
          processing_error: error.message || 'Processing failed'
        })
        .eq('id', documentId);
      
      throw error;
    }
  }

  /**
   * Get knowledge context for AI
   */
  async getKnowledgeContext(
    userId: string,
    businessId?: string,
    category?: string,
    limit: number = 10
  ): Promise<KnowledgeContext[]> {
    try {
      const { data, error } = await supabase.rpc('get_knowledge_context', {
        p_user_id: userId,
        p_business_id: businessId || null,
        p_category: category || null,
        p_limit: limit
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get knowledge context:', error);
      return [];
    }
  }

  /**
   * Search knowledge base with semantic similarity based on user query
   * This is the SMART version that actually finds relevant documents
   */
  async searchKnowledge(
    userId: string,
    query: string,
    businessId?: string,
    limit: number = 5
  ): Promise<KnowledgeContext[]> {
    try {
      console.log(`[Knowledge] Searching for: "${query.slice(0, 100)}..."`);
      
      // Try semantic search first (if embeddings are set up)
      const { data, error } = await supabase.rpc('search_knowledge_semantic', {
        p_user_id: userId,
        p_query: query,
        p_business_id: businessId || null,
        p_limit: limit
      });

      if (error) {
        console.warn('[Knowledge] Semantic search not available, falling back to text search:', error.message);
        // Fallback to text search
        return await this.searchKnowledgeText(userId, query, businessId, limit);
      }
      
      console.log(`[Knowledge] Found ${data?.length || 0} relevant documents`);
      return data || [];
    } catch (error) {
      console.error('[Knowledge] Search failed:', error);
      // Final fallback: return recent documents
      return await this.getKnowledgeContext(userId, businessId, undefined, limit);
    }
  }

  /**
   * Fallback text-based search when semantic search isn't available
   */
  private async searchKnowledgeText(
    userId: string,
    query: string,
    businessId?: string,
    limit: number = 5
  ): Promise<KnowledgeContext[]> {
    try {
      // Simple keyword search in extracted_content
      let dbQuery = supabase
        .from('knowledge_documents')
        .select('id, file_name, category, extracted_content, tags')
        .eq('user_id', userId)
        .eq('processing_status', 'completed')
        .not('extracted_content', 'is', null)
        .limit(limit);

      if (businessId) {
        dbQuery = dbQuery.eq('business_id', businessId);
      }

      // Add text search filter
      const keywords = query.toLowerCase().split(' ').filter(w => w.length > 3);
      if (keywords.length > 0) {
        dbQuery = dbQuery.or(
          keywords.map(keyword => `extracted_content.ilike.%${keyword}%`).join(',')
        );
      }

      const { data, error } = await dbQuery;

      if (error) throw error;

      // Convert to KnowledgeContext format
      return (data || []).map(doc => ({
        document_name: doc.file_name,
        category: doc.category || 'General',
        content: doc.extracted_content || '',
        tags: doc.tags || []
      }));
    } catch (error) {
      console.error('[Knowledge] Text search failed:', error);
      return [];
    }
  }

  /**
   * Build comprehensive business context
   */
  async buildBusinessContext(userId: string, businessId: string): Promise<any> {
    try {
      const { data, error } = await supabase.rpc('build_business_context', {
        p_user_id: userId,
        p_business_id: businessId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to build business context:', error);
      return null;
    }
  }

  /**
   * Format knowledge context for AI prompt
   */
  formatKnowledgeForPrompt(contexts: KnowledgeContext[]): string {
    if (contexts.length === 0) {
      return '';
    }

    let prompt = '\n\n=== KNOWLEDGE BASE CONTEXT ===\n';
    prompt += 'The following documents contain relevant information about the user\'s business:\n\n';

    contexts.forEach((ctx, index) => {
      prompt += `Document ${index + 1}: ${ctx.document_name}\n`;
      prompt += `Category: ${ctx.category}\n`;
      if (ctx.tags && ctx.tags.length > 0) {
        prompt += `Tags: ${ctx.tags.join(', ')}\n`;
      }
      prompt += `Content:\n${ctx.content}\n\n`;
      prompt += '---\n\n';
    });

    prompt += 'Please reference the above knowledge base when answering questions.\n';
    prompt += '=== END KNOWLEDGE BASE ===\n\n';

    return prompt;
  }
}

export const knowledgeService = new KnowledgeService();
